import type { FC, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import ButtonDropdown from 'src/components/button-dropdown';

export type ActionItem = {
  text: string;
  id: string;
  disabled?: boolean;
  disabledReason?: string;
  onItemClick: () => Promise<void> | void;
  iconSvg?: ReactNode;
};

export type Props = {
  buttonText: string;
  items: ActionItem[];
  variant?: 'normal' | 'primary';
  testId?: string;
  disabled?: boolean;
  /**
   * Fired on a trigger gesture that opens the dropdown — mousedown, or Enter /
   * Space / ArrowDown. Opt-in: ButtonDropdown exposes no open-state callback,
   * so this is detected with capture-phase handlers on a wrapping span and
   * reflects open *intent*, not a verified closed→open transition. Events from
   * the expanded menu (`[role="menu"]`) are ignored so it fires only for the
   * trigger, not for menu interactions.
   */
  onOpen?: () => void;
};

const isFromMenu = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest('[role="menu"]') !== null;

const ActionsButton: FC<Props> = ({
  buttonText,
  items,
  variant,
  testId,
  disabled = false,
  onOpen,
}) => {
  const dropdown = (
    <ButtonDropdown
      disabled={disabled}
      data-testid={testId}
      items={items}
      variant={variant || 'normal'}
      onItemClick={(event) => {
        items.find((option) => option.id === event.detail.id)?.onItemClick();
      }}
    >
      {buttonText}
    </ButtonDropdown>
  );

  // Nothing to fire, or disabled (can't open) — render the bare dropdown
  // unchanged; only wrap with the capture handlers when there's an `onOpen`.
  if (!onOpen || disabled) {
    return dropdown;
  }

  const handleMouseDownCapture = (event: MouseEvent<HTMLSpanElement>) => {
    if (isFromMenu(event.target)) {
      return;
    }
    onOpen();
  };

  const handleKeyDownCapture = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (
      event.key !== 'Enter' &&
      event.key !== ' ' &&
      event.key !== 'ArrowDown'
    ) {
      return;
    }
    if (isFromMenu(event.target)) {
      return;
    }
    onOpen();
  };

  return (
    <span
      onMouseDownCapture={handleMouseDownCapture}
      onKeyDownCapture={handleKeyDownCapture}
    >
      {dropdown}
    </span>
  );
};

export default ActionsButton;
